import classNames from 'classnames';
import { useEffect, useState } from 'react';
import React from 'react';
import { useQueryClient } from 'react-query';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import Typography from '@/components/atoms/typography/typography';
import AlertBanner from '@/components/molecules/alert-banner/alert-banner';
import Button from '@/components/molecules/button/button';
import UserAvatar, {
  BasicPerson,
} from '@/components/molecules/user-avatar/user-avatar';
import PlaneSeat from '@/components/organisms/plane-seat/plane-seat';
import TabMenu from '@/components/organisms/tab-menu/tab-menu';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import useMutationAddPreBookingSeat from '@/integrations/booking/pre-booking/seats/add-seat/add-seat.hooks';
import { QueryId } from '@/integrations/content/utils';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import {
  getPassengerByUniquePaxHash,
  getRowLabelPositions,
  getSeatBlockLinePositions,
  getSeatNameByPassengerHash,
  getSeatPriceByPassengerHash,
  RowLabelPosition,
  SeatBlockLinePositions,
} from './seat-selection-modal-utils/seat-selection-modal-utils';
import styles from './seat-selection-modal.module.scss';

export interface SeatSelectionModalProps {
  /** The class name */
  className?: string;
  /** Headline */
  headline?: string;
  /** The parameters to call the hooks */
  identifiers: Content.Components['IBookingHeadlessParameters'] & {
    id: string;
    pageId: string;
  };
  /** Indicates whether the component is used in a webview */
  isInWebview?: boolean;
  /** Indicates whether the modal is open. */
  isOpen?: boolean;
  /** The callback to handle the close event. */
  onClose?: () => void;
  /** Transport seatings */
  transportSeatings?: Content.Components['TransportSeatingDto'][];
}

const SeatSelectionModal = ({
  className,
  headline,
  identifiers,
  isInWebview = false,
  isOpen = true,
  onClose,
  transportSeatings,
}: SeatSelectionModalProps) => {
  const { t } = useI18n();
  const { brand } = usePageDetailsContext();
  const queryClient = useQueryClient();

  const [activeTransportSeatingIndex, setActiveTransportSeatingIndex] =
    useState<number>(0);

  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const [rowLabels, setRowLabels] = useState<RowLabelPosition[]>([]);
  const [seatBlockLines, setSeatBlockLines] =
    useState<SeatBlockLinePositions | null>(null);

  const [activePassenger, setActivePassenger] = useState<
    Content.Components['TransportPassengerDto'] | undefined
  >(undefined);

  const [updatedTransportSeatings, setUpdatedTransportSeatings] = useState<
    Content.Components['TransportSeatingDto'][]
  >([]);

  const [initSetup, setInitSetup] = useState(false);
  const currencyFormatter = useCurrencyFormatter();

  const invalidationKeys = [QueryId.SEAT_SELECTION];

  const {
    isLoading: isMutatingAddPreBookingSeat,
    mutateAsync: mutateAsyncAddPreBookingSeat,
  } = useMutationAddPreBookingSeat();

  const getBasicPerson = (
    passenger: Content.Properties.TransportPassenger,
  ): BasicPerson => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return {
        firstName: placeholders.firstName,
        lastName: placeholders.lastName,
      };
    }

    return { firstName, lastName };
  };

  const clearModal = () => {
    const filteredSeatings =
      transportSeatings?.filter(
        transportSeating =>
          transportSeating.sections && transportSeating.sections.length > 0,
      ) ?? [];

    setUpdatedTransportSeatings(filteredSeatings);
    setActivePassenger(filteredSeatings[0]?.passengers?.[0]);
    setActiveTransportSeatingIndex(0);
    setRowLabels([]);
    setSeatBlockLines(null);
    setShowErrorMessage(false);
  };

  const getNameDisplay = (
    passenger: Content.Properties.TransportPassenger,
  ): string => {
    const { firstName, lastName, placeholders } = passenger;

    if (!firstName && !lastName) {
      return `${placeholders.firstName} ${placeholders.lastName}`;
    }

    return `${firstName} ${lastName}`;
  };

  const setUpActiveTransportSeating = (activeIndex: number) => {
    if (updatedTransportSeatings === undefined) return;

    // Ensure the provided index points to a seating with sections.
    const validIndex =
      (updatedTransportSeatings[activeIndex]?.sections?.length ?? 0) > 0
        ? activeIndex
        : updatedTransportSeatings.findIndex(
            ts => ts.sections && ts.sections.length > 0,
          );

    if (validIndex === -1) return;

    setActiveTransportSeatingIndex(validIndex);

    setActivePassenger(updatedTransportSeatings[validIndex]?.passengers?.[0]);

    setRowLabels(
      getRowLabelPositions(
        updatedTransportSeatings[validIndex]?.sections?.[0]?.seats ?? [],
      ),
    );

    setSeatBlockLines(
      getSeatBlockLinePositions(
        updatedTransportSeatings[validIndex]?.sections?.[0]?.seats ?? [],
        -2, // leftOffset
        8, // rightOffset
      ),
    );
  };

  useEffect(() => {
    setUpdatedTransportSeatings(
      transportSeatings?.filter(
        transportSeating =>
          transportSeating.sections && transportSeating.sections.length > 0,
      ) ?? [],
    );
    setInitSetup(true);
  }, [transportSeatings]);

  useEffect(() => {
    if (
      initSetup &&
      updatedTransportSeatings &&
      updatedTransportSeatings.length > 0
    ) {
      // Find first transport seating that has sections
      const firstValidIndex = updatedTransportSeatings.findIndex(
        transportSeating =>
          transportSeating.sections && transportSeating.sections.length > 0,
      );

      if (firstValidIndex !== -1) {
        setUpActiveTransportSeating(firstValidIndex);
      }

      setInitSetup(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTransportSeatings]);

  const handleOnClickPassenger = (
    passenger: Content.Components['TransportPassengerDto'],
  ) => {
    setActivePassenger(passenger);
  };

  const handleOnNextFlightClick = () => {
    if (updatedTransportSeatings.length === 0) return;

    const nextIndex = activeTransportSeatingIndex + 1;

    if (nextIndex < updatedTransportSeatings.length) {
      setUpActiveTransportSeating(nextIndex);

      const passengers = updatedTransportSeatings[nextIndex]?.passengers;

      passengers && handleOnClickPassenger(passengers[0]);
    }
  };

  const handleOnClickRemoveSeatOption = (
    passenger: Content.Components['TransportPassengerDto'],
  ) => {
    if (!updatedTransportSeatings[activeTransportSeatingIndex]) return;

    setUpdatedTransportSeatings(prev => {
      if (!prev) return prev;

      return prev.map(transportSeating => {
        if (
          transportSeating !==
          updatedTransportSeatings[activeTransportSeatingIndex]
        )
          return transportSeating;

        const updatedSections = transportSeating.sections.map(section => {
          const updatedSeats = section.seats.map(seat => {
            if (seat.uniquePaxHash === passenger.uniquePaxHash) {
              // Unassign this passenger from this seat
              return { ...seat, isSelected: false, uniquePaxHash: '' };
            }

            return seat;
          });

          return { ...section, seats: updatedSeats };
        });

        return { ...transportSeating, sections: updatedSections };
      });
    });
  };

  const handleOnClickSeatOption = (
    selectedSeat: Content.Components['SeatDto'],
  ): void => {
    if (
      !activePassenger ||
      !updatedTransportSeatings[activeTransportSeatingIndex]
    )
      return;

    setUpdatedTransportSeatings(previousTransportSeatings => {
      if (!previousTransportSeatings) return previousTransportSeatings;

      return previousTransportSeatings.map(transportSeating => {
        if (
          transportSeating !==
          updatedTransportSeatings[activeTransportSeatingIndex]
        )
          return transportSeating;

        const updatedSections = transportSeating.sections.map(
          (section, sectionIndex) => {
            if (sectionIndex !== 0) return section;

            const updatedSeats = section.seats.map(seat => {
              // First unassign the passenger if they already had a seat
              if (seat.uniquePaxHash === activePassenger.uniquePaxHash) {
                return { ...seat, isSelected: false, uniquePaxHash: '' };
              }

              // Then assign the new seat
              if (seat.layoutSectionMapId === selectedSeat.layoutSectionMapId) {
                return {
                  ...seat,
                  isSelected: true,
                  uniquePaxHash: activePassenger.uniquePaxHash,
                };
              }

              return seat;
            });

            return { ...section, seats: updatedSeats };
          },
        );

        return { ...transportSeating, sections: updatedSections };
      });
    });

    // Find the next passenger without a seat, starting *after* the current passenger
    const passengers =
      updatedTransportSeatings[activeTransportSeatingIndex]?.passengers ?? [];

    const currentIndex = passengers.findIndex(
      passenger => passenger.uniquePaxHash === activePassenger.uniquePaxHash,
    );

    let nextPassenger: Content.Components['TransportPassengerDto'] | undefined;

    for (let index = 1; index <= passengers.length; index++) {
      const idx = (currentIndex + index) % passengers.length;
      const candidate = passengers[idx];
      const hasSeat =
        getSeatNameByPassengerHash(
          candidate.uniquePaxHash,
          updatedTransportSeatings[activeTransportSeatingIndex],
        ) !== null;

      if (!hasSeat) {
        nextPassenger = candidate;
        break;
      }
    }

    if (nextPassenger) {
      handleOnClickPassenger(nextPassenger);
    }
  };

  const handleOnClickSave = async () => {
    if (updatedTransportSeatings) {
      const directions = updatedTransportSeatings.map(transportSeating => ({
        putSeatRequestBodyDtos: transportSeating.sections.flatMap(section =>
          section.seats
            .filter(seat => seat.isSelected)
            .map(seat => ({
              layoutSectionMapId: seat.layoutSectionMapId,
              uniquePaxHash: seat.uniquePaxHash,
            })),
        ),
        travelDirection: { value: transportSeating.travelDirection },
      }));

      const result = await mutateAsyncAddPreBookingSeat({
        brand,
        data: { directions },
        parameters: identifiers,
      });

      const hasError = result?.result === false;

      setShowErrorMessage(hasError);

      // Only close modal and redirect if all results were successful
      if (!hasError) {
        if (isInWebview && window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              status: 'success',
            }),
          );

          return;
        }

        onClose?.();

        // Manually invalidate queries after all mutations complete
        invalidationKeys.forEach(key => {
          queryClient.invalidateQueries(key);
        });
      }
    }
  };

  const renderContent = (): React.ReactNode => {
    return (
      <div className={styles.contentWrapper}>
        {!isInWebview && headline && (
          <Typography className={styles.headline} tagStyle="headingMedium">
            {headline}
          </Typography>
        )}

        {updatedTransportSeatings && updatedTransportSeatings.length > 0 && (
          <TabMenu
            activeItem={{
              type: 'id',
              value:
                updatedTransportSeatings[activeTransportSeatingIndex]?.headline,
            }}
            tabs={updatedTransportSeatings.map((transportSeating, index) => ({
              id: transportSeating.headline,
              onClick: () => setUpActiveTransportSeating(index),
              text: transportSeating.headline,
            }))}
          />
        )}

        <div className={styles.topSection}>
          {updatedTransportSeatings && updatedTransportSeatings.length > 0 && (
            <React.Fragment>
              <div className={styles.seatSelectionWrapper}>
                <div>
                  {showErrorMessage && (
                    <AlertBanner
                      text={t(
                        'items.modules.bookingFlow.seatSelectionValidationErrorMessage',
                      )}
                    />
                  )}
                </div>

                <div className={styles.tabContentWrapper}>
                  <div className={styles.planeDiagramWrapper}>
                    <div
                      style={{
                        minHeight:
                          updatedTransportSeatings[activeTransportSeatingIndex]
                            ?.sections?.[0]?.height,
                        minWidth:
                          updatedTransportSeatings[activeTransportSeatingIndex]
                            ?.sections?.[0]?.width,
                        position: 'relative',
                      }}
                    >
                      {/* Plane side lines */}
                      {seatBlockLines && (
                        <React.Fragment>
                          <div
                            className={styles.seatBlockLine}
                            style={{
                              height:
                                seatBlockLines.leftLine.y2 -
                                seatBlockLines.leftLine.y1,
                              left: seatBlockLines.leftLine.x,
                              position: 'absolute',
                              top: seatBlockLines.leftLine.y1,
                            }}
                          />
                          <div
                            className={styles.seatBlockLine}
                            style={{
                              height:
                                seatBlockLines.rightLine.y2 -
                                seatBlockLines.rightLine.y1,
                              left: seatBlockLines.rightLine.x,
                              position: 'absolute',
                              top: seatBlockLines.rightLine.y1,
                            }}
                          />
                        </React.Fragment>
                      )}

                      {/* Plane design elements */}
                      {updatedTransportSeatings[
                        activeTransportSeatingIndex
                      ]?.sections?.[0]?.designElements?.map(designElement => (
                        <div
                          key={designElement.layoutSectionMapId}
                          className={styles.designElement}
                          style={{
                            left: designElement.positionX,
                            top: designElement.positionY,
                          }}
                        >
                          <Image
                            alt={
                              designElement?.layoutSectionMapId.toString() ?? ''
                            }
                            src={designElement.imagePath}
                            unoptimized
                            width={designElement.noseWidth ?? 55}
                          />
                        </div>
                      ))}

                      {/* Plane seats */}
                      {updatedTransportSeatings[
                        activeTransportSeatingIndex
                      ]?.sections?.[0]?.seats?.map(seat => (
                        <PlaneSeat
                          key={seat.layoutSectionMapId}
                          isDisabled={
                            seat.uniquePaxHash !== '' &&
                            seat.uniquePaxHash !==
                              activePassenger?.uniquePaxHash
                          }
                          onClick={() => handleOnClickSeatOption(seat)}
                          passenger={
                            getPassengerByUniquePaxHash(
                              seat.uniquePaxHash,
                              updatedTransportSeatings[
                                activeTransportSeatingIndex
                              ],
                            ) ?? undefined
                          }
                          seat={seat}
                          seatType={updatedTransportSeatings[
                            activeTransportSeatingIndex
                          ]?.seatTypes?.find(
                            seatType => seatType.seatTypeId === seat.seatTypeId,
                          )}
                          style={{
                            left: seat.positionX,
                            position: 'absolute',
                            top: seat.positionY,
                          }}
                        />
                      ))}

                      {/* Plane row labels */}
                      {rowLabels.map(label => (
                        <div
                          key={label.rowNumber}
                          className={styles.rowLabel}
                          style={{
                            left: label.x,
                            position: 'absolute',
                            top: label.y,
                          }}
                        >
                          {label.rowNumber}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.seatOptionsWrapper}>
                    <div className={styles.passengersWrapper}>
                      <Typography tagStyle="headingSmall">
                        {t('items.modules.bookingFlow.passengers')}
                      </Typography>

                      {updatedTransportSeatings[
                        activeTransportSeatingIndex
                      ] && (
                        <div className={styles.passengerItems}>
                          {updatedTransportSeatings[
                            activeTransportSeatingIndex
                          ]?.passengers?.map(passenger => (
                            <button
                              key={passenger.uniquePaxHash}
                              className={classNames(styles.passengerItem, {
                                [styles.isActive]:
                                  passenger.uniquePaxHash ===
                                  activePassenger?.uniquePaxHash,
                              })}
                              onClick={() => handleOnClickPassenger(passenger)}
                              tabIndex={0}
                              type="button"
                            >
                              <div className={styles.content}>
                                <UserAvatar
                                  passenger={getBasicPerson(passenger)}
                                  size="medium"
                                  tooltipPosition="right"
                                />

                                <div className={styles.passengerNameWrapper}>
                                  <Typography
                                    className={styles.passengerName}
                                    tagStyle="bodyMedium"
                                  >
                                    {getNameDisplay(passenger)}
                                  </Typography>

                                  {getSeatNameByPassengerHash(
                                    passenger.uniquePaxHash,
                                    updatedTransportSeatings[
                                      activeTransportSeatingIndex
                                    ],
                                  ) !== null ? (
                                    <Typography tagStyle="bodyMediumBold">
                                      {`${t('items.modules.bookingFlow.chosen')}: ${getSeatNameByPassengerHash(
                                        passenger.uniquePaxHash,
                                        updatedTransportSeatings[
                                          activeTransportSeatingIndex
                                        ],
                                      )}`}
                                    </Typography>
                                  ) : (
                                    <Typography
                                      className={styles.notSelected}
                                      tagStyle="bodyMedium"
                                    >
                                      {t(
                                        'items.modules.bookingFlow.noneSelected',
                                      )}
                                    </Typography>
                                  )}
                                </div>
                              </div>

                              {getSeatNameByPassengerHash(
                                passenger.uniquePaxHash,
                                updatedTransportSeatings[
                                  activeTransportSeatingIndex
                                ],
                              ) !== null && (
                                <div className={styles.priceWrapper}>
                                  <Typography
                                    tag="label"
                                    tagStyle="bodyMediumBold"
                                  >
                                    {currencyFormatter(
                                      getSeatPriceByPassengerHash(
                                        passenger.uniquePaxHash,
                                        updatedTransportSeatings[
                                          activeTransportSeatingIndex
                                        ],
                                      ),
                                    )}
                                  </Typography>

                                  <button
                                    aria-label={t('items.common.words.close')}
                                    className={styles.icon}
                                    onClick={() =>
                                      handleOnClickRemoveSeatOption(passenger)
                                    }
                                    type="button"
                                  >
                                    <Icon name="Close" size={14} />
                                  </button>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.seatTypesWrapper}>
                      <Typography tagStyle="headingSmall">
                        {t('items.modules.bookingFlow.seatTypes')}
                      </Typography>

                      <div className={styles.seatItems}>
                        {updatedTransportSeatings[
                          activeTransportSeatingIndex
                        ]?.seatTypes?.map(seatType => (
                          <div
                            key={seatType.seatTypeId}
                            className={styles.seatItem}
                          >
                            <PlaneSeat seat={seatType} />

                            <div className={styles.seatItemDetails}>
                              <Typography tag="label" tagStyle="bodyMediumBold">
                                {seatType.name}
                              </Typography>

                              <Typography tag="label" tagStyle="bodySmall">
                                {seatType.description}
                              </Typography>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          )}
        </div>

        <div className={styles.bottomSection}>
          <Button
            className={styles.cta}
            onClick={onClose}
            text={t('items.common.words.cancel')}
            variant="secondary"
          />

          <Button
            className={styles.cta}
            isLoading={isMutatingAddPreBookingSeat}
            onClick={
              activeTransportSeatingIndex ===
              updatedTransportSeatings.length - 1
                ? handleOnClickSave
                : handleOnNextFlightClick
            }
            text={
              activeTransportSeatingIndex ===
              updatedTransportSeatings.length - 1
                ? t('items.modules.bookingFlow.save')
                : t('items.modules.bookingFlow.nextFlight')
            }
            variant="primary"
          />
        </div>
      </div>
    );
  };

  if (isInWebview) {
    return (
      <div className={classNames(styles.component, styles.webviewWrapper)}>
        {renderContent()}
      </div>
    );
  }

  return (
    <Lightbox
      className={classNames(styles.component, className)}
      fullscreen
      isCentered
      isOpen={isOpen}
      onClose={() => {
        onClose?.();
        clearModal();
      }}
      size="large"
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={styles.lightboxInnerWrapper}>{renderContent()}</div>
    </Lightbox>
  );
};

SeatSelectionModal.displayName = 'SeatSelectionModal';

export default SeatSelectionModal;
